import { getV8MessageQueueKafkaDetailFunction } from '@/api-client/message-queue/queries/kafka-controller/kafka-controller';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import { useMessage } from '@/common/inject/useMessage';
import { useInternational } from '@/common/locale';
import { useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { getSlideDetailStore, TOTAL_DETAIL } from '@/common/stores/slide-detail';
import {
  SlideDetailAdapterParams,
  SlideDetailAdapterResult,
  SlideDetailAdapterResultKey,
} from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import { RolePermissionKeyType } from '@/config/views/permission/rolePermission/rolePermission.type';
import {
  DEFINED_KAFKA_INSTANCE_TYPE,
  KAFKA_INSTANCE_TYPE,
  KafkaInstanceKind,
  KafkaInstanceKindFromMainView,
} from '@/messageQueue/utils/kafka/define';
import { DefinedKafkaInstanceType } from '@/messageQueue/utils/kafka/types';
import { getRequiredMap } from '@/common/utils/core/detailFieldMeta.utils';
import { ref } from 'vue';
import { z } from 'zod';

const kafkaDetailInputSchema = z
  .object({
    targetId: z.string().optional(),
    instanceId: z.string().optional(),
    targetName: z.string().optional(),
    rawData: z.record(z.any()).optional(),
    instanceType: z.preprocess(
      (val) => (typeof val === 'string' ? val.toLowerCase() : val),
      z.nativeEnum(DEFINED_KAFKA_INSTANCE_TYPE),
    ),
    instanceKind: z.nativeEnum(KafkaInstanceKind).optional(),
    instanceName: z.string().optional(),
    title: z.string().optional(),
    topic: z.string().optional(),
    brokerTopicIds: z.array(z.string()).optional(),
    entrySource: z
      .object({
        entry: z.preprocess(
          (val) => (typeof val === 'string' ? val.toLowerCase() : val),
          z.enum(['cluster', 'broker']),
        ),
        entrySourceId: z.string().min(1),
      })
      .optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
  })
  .transform(({ targetId, instanceId, targetName, rawData, instanceName, ...rest }) => ({
    ...rest,
    instanceId: instanceId || rawData?.instanceId || targetId || '',
    instanceName: instanceName || targetName || rawData?.instanceName || rawData?.targetName,
  }))
  .pipe(
    z.object({
      instanceId: z.string().min(1),
      instanceType: z.preprocess(
        (val) => (typeof val === 'string' ? val.toLowerCase() : val),
        z.nativeEnum(DEFINED_KAFKA_INSTANCE_TYPE),
      ),
      instanceKind: z.nativeEnum(KafkaInstanceKind).optional(),
      instanceName: z.string().optional(),
      title: z.string().optional(),
      topic: z.string().optional(),
      brokerTopicIds: z.array(z.string()).optional(),
      entrySource: z
        .object({
          entry: z.preprocess(
            (val) => (typeof val === 'string' ? val.toLowerCase() : val),
            z.enum(['cluster', 'broker']),
          ),
          entrySourceId: z.string().min(1),
        })
        .optional(),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export type KafkaDetailInput = z.input<typeof kafkaDetailInputSchema>;
type KafkaDetailOutput = z.output<typeof kafkaDetailInputSchema>;

export const kafkaDetailRequiredMap = getRequiredMap(kafkaDetailInputSchema);

export const useKafkaDetailAdapter = (rolePermissionKey?: RolePermissionKeyType) => {
  const DEFAULT_ROLE_PERMISSION_KEY = ROLE_PERMISSION_KEY.MONITORING.MONITORING_MESSAGEQUEUE_KAFKA;

  const isSlideDetailOpened = ref(false);
  const { showErrorMsg } = useMessage();
  const { t } = useInternational();
  const { addTabPanel, isDetailSlidePermissionDenied } = getSlideDetailStore();
  const { checkIsValidTarget } = useTargetPermissionCheck();

  const fetchTopicDetail = async (instanceId: string) => {
    try {
      const {
        data: { data = [] },
      } = await getV8MessageQueueKafkaDetailFunction({ targetId: instanceId });

      if (!data.length) {
        return null;
      }

      const entryType =
        data[0]?.type === 'kafka_cluster_topic'
          ? KAFKA_INSTANCE_TYPE.CLUSTER
          : KAFKA_INSTANCE_TYPE.BROKER;

      return {
        instanceId: data[0]?.clusterTopicId ?? '',
        brokerTopicIds: data[0]?.brokerTopicIds ?? [],
        entrySource: {
          entry: entryType,
          entrySourceId: data[0]?.targetId ?? '',
        },
      };
    } catch (error) {
      console.error(error);
      return null;
    }
  };

  const getDetailType = (instanceType: DefinedKafkaInstanceType) => {
    switch (instanceType) {
      case DEFINED_KAFKA_INSTANCE_TYPE.CLUSTER:
      case DEFINED_KAFKA_INSTANCE_TYPE.CLUSTER_ZOOKEEPER:
        return TOTAL_DETAIL.KAFKA_CLUSTER;
      case DEFINED_KAFKA_INSTANCE_TYPE.BROKER:
        return TOTAL_DETAIL.KAFKA_BROKER;
      case DEFINED_KAFKA_INSTANCE_TYPE.TOPIC:
        return TOTAL_DETAIL.KAFKA_TOPIC;
      default:
        return TOTAL_DETAIL.KAFKA_CLUSTER;
    }
  };

  const normalizeParams = async (
    params: KafkaDetailInput,
    showErrorMessage: boolean,
  ): Promise<KafkaDetailOutput | null> => {
    // Zod 검증 및 변환
    const result = kafkaDetailInputSchema.safeParse(params);

    if (!result.success) {
      if (showErrorMessage) {
        const missingParams = result.error.issues?.map((issue) => issue.path[0]).join(', ');
        showErrorMsg({
          msgStr: t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', { value: missingParams }),
        });
      }
      return null;
    }

    let normalized = result.data;

    // 외부에서 토픽 디테일 접근 시 추가 정보 로딩
    if (
      normalized.instanceType === KAFKA_INSTANCE_TYPE.TOPIC &&
      normalized.instanceId &&
      !normalized.entrySource
    ) {
      const topicDetail = await fetchTopicDetail(normalized.instanceId);
      if (topicDetail) {
        normalized = {
          ...normalized,
          instanceId: topicDetail.instanceId || normalized.instanceId,
          brokerTopicIds: topicDetail.brokerTopicIds,
          entrySource: topicDetail.entrySource,
        };
      }
    }

    return normalized;
  };

  // Kafka Main View > Detail 연계 조회시 타입 판단
  const getTargetKind = (
    type: KafkaDetailOutput['instanceType'],
    entrySource?: KafkaDetailOutput['entrySource'],
  ) => {
    if (type === 'topic') {
      if (entrySource && entrySource?.entry === 'cluster') {
        return KafkaInstanceKindFromMainView['kafka_cluster_topic'];
      } else if (entrySource && entrySource?.entry === 'broker') {
        return KafkaInstanceKindFromMainView['kafka_broker_topic'];
      }
    }

    return KafkaInstanceKindFromMainView[type];
  };

  const validateKafkaDetail = async (
    params: KafkaDetailInput,
    options: {
      showErrorMessage: boolean;
      checkPermission?: boolean;
      checkParams?: boolean;
      checkTarget?: boolean;
    },
  ): Promise<{
    reason: SlideDetailAdapterResultKey;
    normalizedParams?: KafkaDetailOutput;
  }> => {
    const { checkPermission = true, checkParams = true, checkTarget = true } = options;

    // 1. 권한 체크
    if (
      checkPermission &&
      isDetailSlidePermissionDenied({
        rolePermissionKey: rolePermissionKey ?? DEFAULT_ROLE_PERMISSION_KEY,
        type: getDetailType(params.instanceType as DefinedKafkaInstanceType),
        showError: options.showErrorMessage,
      })
    ) {
      return { reason: 'permission_denied' };
    }

    // 2. 파라미터 검증
    const normalizedResult = await normalizeParams(
      params,
      checkParams ? options.showErrorMessage : false,
    );

    if (!normalizedResult) {
      return { reason: 'invalid_params' };
    }

    // 3. 토픽 데이터 유효성 재확인
    if (
      normalizedResult.instanceType === KAFKA_INSTANCE_TYPE.TOPIC &&
      (!normalizedResult.brokerTopicIds || !normalizedResult.entrySource)
    ) {
      if (options.showErrorMessage) {
        showErrorMsg({ msgStr: t('ERROR.NO_DETAIL_DATA') });
      }
      return { reason: 'invalid_params' };
    }

    // 4. 타겟 유효성 검증
    if (checkTarget) {
      const targetKind = normalizedResult.instanceKind
        ? normalizedResult.instanceKind
        : getTargetKind(normalizedResult.instanceType, normalizedResult.entrySource);

      const targetId =
        normalizedResult.entrySource?.entry === 'broker'
          ? (normalizedResult.brokerTopicIds?.[0] ?? '')
          : normalizedResult.instanceId;

      const targetValidation = await checkIsValidTarget(
        { targetId, targetKind },
        { showErrorMessage: options.showErrorMessage },
      );
      if (!targetValidation.valid) {
        return { reason: targetValidation.reason };
      }
    }

    return { reason: 'ok', normalizedParams: normalizedResult };
  };

  const openKafkaDetail = async ({
    params,
    options,
    state,
    currentState,
    init,
  }: SlideDetailAdapterParams<KafkaDetailInput>): Promise<SlideDetailAdapterResult> => {
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      runTargetValidation = true,
    } = options ?? {};

    const { reason, normalizedParams } = await validateKafkaDetail(params, {
      showErrorMessage,
      checkPermission: runCheckPermission,
      checkTarget: runTargetValidation,
    });
    if (reason !== 'ok' || !normalizedParams) return createSlideDetailResult(reason);

    const {
      instanceId,
      instanceType,
      instanceName,
      title,
      brokerTopicIds,
      entrySource,
      fromTime,
      toTime,
    } = normalizedParams;

    const result = addTabPanel(
      {
        type: getDetailType(instanceType),
        name: title || instanceName || '',
        addition: {
          instanceId,
          instanceType,
          ...(instanceType === KAFKA_INSTANCE_TYPE.TOPIC && {
            topic: instanceName || '',
            brokerTopicIds,
            entrySource,
          }),
          fromTime,
          toTime,
        },
        state,
        init,
      },
      currentState,
    );

    isSlideDetailOpened.value = getSlideDetailStore().isOpened;
    return createSlideDetailResult(result);
  };

  return {
    isSlideDetailOpened,
    openKafkaDetail,
  };
};
